import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import type { FilePresence, FilePresenceList } from '@/types/file/FilePresence'
import type { UUID } from '@/types/Id'

import DataLoader from '@/vendor/dataloader'

/** The endpoint takes a thousand; a hundred is more files than a screen shows. */
const MAX_BATCH_SIZE = 100

/** How long a batch stays open. Every file on screen asks on the same tick. */
const BATCH_WINDOW_MS = 50

const api = {
  readPresence: async (
    fileIds: readonly UUID[],
    accessToken: string,
  ): Promise<FilePresenceList> => {
    const res = await fetch(`${API_URL}/api/v1/files/presence`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ file_ids: fileIds }),
    })

    if (!res.ok) {
      throw new SweetspotError({
        name: 'FETCH_ERROR',
        message: res.statusText,
        cause: { status: res.status },
      })
    }

    return (await res.json()) as FilePresenceList
  },
}

const batchFilePresence = async (
  fileIds: readonly UUID[],
  accessToken: string,
): Promise<(FilePresence | null)[]> => {
  const { data } = await api.readPresence(fileIds, accessToken)
  const byFile = new Map(
    data.map((presence) => [presence.file_id, presence] as const),
  )

  // A file missing from the answer is a file nobody is in.
  return fileIds.map((fileId) => byFile.get(fileId) ?? null)
}

/**
 * Who has each file open, asked one file at a time and sent as one request, so a
 * row asks about itself without knowing what else is on screen.
 */
export const createFilePresenceLoader = (accessToken: string) => {
  return new DataLoader<UUID, FilePresence | null>(
    (fileIds) => batchFilePresence(fileIds, accessToken),
    {
      // React Query already holds the answers and decides when they are stale;
      // caching here too would pin the first answer for the life of the page.
      cache: false,
      maxBatchSize: MAX_BATCH_SIZE,
      batchScheduleFn: (callback) => setTimeout(callback, BATCH_WINDOW_MS),
    },
  )
}
